import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { getDisponibilidadHora } from '../../../services/analitica';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { HourlyDetailTableFormatDayHeader } from './HourlyDetailTableFormatDayHeader';
import { toNullableNumber, isValidNumber } from '../../../utils/numbers';

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) =>
  `${String(i).padStart(2, '0')}:00`
);

const MARKED_HOUR_BG = 'rgba(111, 66, 193, 0.24)';

const getAvailabilityBg = (value, { skipGreen = false, alwaysColor = false } = {}) => {
  if (value == null) return null;
  if (value >= 98) {
    if (skipGreen) return null;
    return 'rgba(64, 187, 2, 0.2)';  // green
  }
  if (value >= 95) return 'rgba(254, 188, 9, 0.2)'; // yellow
  return 'rgba(178, 19, 34, 0.2)'; // red
};

function HourlyDetailTable({ open, onClose, objective, monthIndex, type, colors, locale }) {
  const { t } = useTranslation();
  const [hourlyData, setHourlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const monthRange = useMemo(() => {
    const monthKey = objective?.months?.[monthIndex]; // "YYYY-MM"
    if (!monthKey) return null;
    const [y, m] = monthKey.split('-').map(Number);
    const firstDay = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const lastDayStr = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from: firstDay, to: lastDayStr, year: y, month: m, prefix: `${y}-${String(m).padStart(2, '0')}` };
  }, [objective, monthIndex]);

  useEffect(() => {
    if (!open || !monthRange || !objective) return;

    let cancelled = false;
    const fetchHourly = async () => {
      setLoading(true);
      setError(null);
      try {
        const fecha = `${monthRange.from},${monthRange.to}`;
        const raw = await getDisponibilidadHora(fecha, {
          canal: objective.canalId,
          tipo_servicio: objective.tipoServicio,
        });
        if (!cancelled) {
          setHourlyData(Array.isArray(raw) ? raw : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[HourlyDetailTable] Error fetching hourly data:', err);
          setError(t('ind.hourly.error'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHourly();
    return () => { cancelled = true; };
  }, [open, monthRange, objective]);

  const monthName = useMemo(() => {
    if (!monthRange) return '';
    const loc = locale || 'es';
    const d = new Date(monthRange.year, monthRange.month - 1);
    const name = d.toLocaleDateString(loc, { month: 'long', year: 'numeric' });
    return name.charAt(0).toUpperCase() + name.slice(1);
  }, [monthRange, locale]);

  // Organiza data: { days[], grid[hour][dayIdx], avgReal, avgProgramado }
  // Acumulado Real = promedio de los porcentajes diarios (promedio por día, luego promedio de días)
  // Acumulado Programado = promedio de disponibilidad_programada por día (disponibilidad - marcados)
  const { days, grid, avgReal, avgProgramado, dayRealAvg, dayProgAvg, p95, dayAvg, dayP95, dayCodes } = useMemo(() => {
    if (!hourlyData?.length) return { days: [], grid: [], avgReal: 0, avgProgramado: 0, dayRealAvg: [], dayProgAvg: [], p95: 0, dayAvg: [], dayP95: [], dayCodes: {} };

    const field = type === 'disponibilidad' ? 'disponibilidad' : 'tiempo';
    const fieldProg = 'disponibilidad_programada';

    // TODO: Filtro por prefijo de mes necesario para Parquet (el API devuelve días de meses adyacentes).
    // Si se vuelve a JSON, evaluar si este filtro sigue siendo necesario o se puede quitar.
    const monthPrefix = monthRange ? monthRange.prefix : null;
    const daySet = new Set();
    hourlyData.forEach(r => {
      if (monthPrefix && !String(r.dia).startsWith(monthPrefix)) return;
      daySet.add(r.dia);
    });
    const sortedDays = Array.from(daySet).sort();

    const dayIdx = {};
    sortedDays.forEach((d, i) => { dayIdx[d] = i; });

    const hourGrid = Array.from({ length: 24 }, () =>
      new Array(sortedDays.length).fill(null)
    );

    // Acumulados: agrupa por día primero, luego promedia
    const dayAccum = {}; // { dia: { sumReal, countReal, sumProg, countProg } }
    const dayCodesMap = {}; // { dia: Set<string> }

    hourlyData.forEach(r => {
      // TODO: mismo filtro Parquet — quitar si se vuelve a JSON
      if (monthPrefix && !String(r.dia).startsWith(monthPrefix)) return;
      const h = Number(r.hora);
      const di = dayIdx[r.dia];
      if (h >= 0 && h < 24 && di !== undefined) {
        const realVal = toNullableNumber(r[field]);
        const progVal = toNullableNumber(r[fieldProg]);
        // Hora "con marcados": el Programado es mayor que el Real (hubo tiempo
        // marcado/excluido que el Programado no cuenta como caída).
        const hasMarked = isValidNumber(progVal)
          && isValidNumber(realVal)
          && progVal > realVal + 0.01;
        hourGrid[h][di] = {
          value: realVal,
          programado: progVal,
          hasMarked,
        };
        if (!dayAccum[r.dia]) dayAccum[r.dia] = { sumReal: 0, countReal: 0, sumProg: 0, countProg: 0 };
        if (isValidNumber(realVal)) {
          dayAccum[r.dia].sumReal += realVal;
          dayAccum[r.dia].countReal++;
        }
        if (isValidNumber(progVal)) {
          dayAccum[r.dia].sumProg += progVal;
          dayAccum[r.dia].countProg++;
        }
      }
      // Recolectar códigos de incidentes por día
      const raw = r.codigos != null ? String(r.codigos).trim() : '';
      if (raw) {
        if (!dayCodesMap[r.dia]) dayCodesMap[r.dia] = new Set();
        raw.split(',').forEach(c => {
          const code = c.trim();
          if (code) dayCodesMap[r.dia].add(code);
        });
      }
    });

    // Promedia: primero % de cada día, luego promedio de esos %
    const dayKeys = Object.keys(dayAccum);
    let totalDayReal = 0;
    let totalDayProg = 0;
    let totalRealDays = 0;
    let totalProgDays = 0;
    dayKeys.forEach(dia => {
      const d = dayAccum[dia];
      if (d.countReal > 0) {
        totalDayReal += d.sumReal / d.countReal;   // % del día (real)
        totalRealDays++;
      }
      if (d.countProg > 0) {
        totalDayProg += d.sumProg / d.countProg;   // % del día (programado = disponibilidad - marcados)
        totalProgDays++;
      }
    });

    // P95: recopila todos los valores reales, ordena ascendentemente, toma el percentil 95
    const allValues = hourlyData
      .map(r => toNullableNumber(r[field]))
      .filter(isValidNumber)
      .sort((a, b) => a - b);
    const p95Index = Math.ceil(allValues.length * 0.95) - 1;
    const p95Value = allValues.length > 0 ? Number(allValues[Math.max(0, p95Index)].toFixed(2)) : 0;

    // Por dia Promedio y P95 para tiempo
    const computedDayAvg = sortedDays.map(d => dayAccum[d] && dayAccum[d].countReal > 0
      ? Number((dayAccum[d].sumReal / dayAccum[d].countReal).toFixed(2))
      : null);
    const computedDayP95 = sortedDays.map((d, idx) => {
      const vals = [];
      for (let h = 0; h < 24; h++) {
        const cell = hourGrid[h][idx];
        if (cell != null && isValidNumber(cell.value)) vals.push(cell.value);
      }
      if (!vals.length) return null;
      vals.sort((a, b) => a - b);
      const i = Math.ceil(vals.length * 0.95) - 1;
      return Number(vals[Math.max(0, i)].toFixed(2));
    });

    return {
      days: sortedDays,
      grid: hourGrid,
      avgReal: totalRealDays > 0 ? Number((totalDayReal / totalRealDays).toFixed(2)) : 0,
      avgProgramado: totalProgDays > 0 ? Number((totalDayProg / totalProgDays).toFixed(2)) : 0,
      dayRealAvg: sortedDays.map(d => dayAccum[d] && dayAccum[d].countReal > 0
        ? Number((dayAccum[d].sumReal / dayAccum[d].countReal).toFixed(2))
        : null),
      dayProgAvg: sortedDays.map(d => dayAccum[d] && dayAccum[d].countProg > 0
        ? Number((dayAccum[d].sumProg / dayAccum[d].countProg).toFixed(2))
        : null),
      p95: p95Value,
      dayAvg: computedDayAvg,
      dayP95: computedDayP95,
      dayCodes: dayCodesMap,
    };
  }, [hourlyData, type, monthRange]);

  const unit = type === 'disponibilidad' ? '%' : 's';

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      style={{ zIndex: 10000 }}
      PaperProps={{
        style: {
          borderRadius: 36,
          padding: '28px 32px 32px',
          maxWidth: '80vw',
          maxHeight: '95vh',
          width: 'auto',
        },
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography style={{ fontWeight: 700, fontSize: '1.25rem', color: '#000' }}>
          {t('ind.hourly.title')}
        </Typography>
        <Box display="flex" alignItems="center" style={{ cursor: 'pointer', gap: 8 }} onClick={onClose}>
          <Typography style={{ fontSize: '0.9rem', color: '#404040', fontWeight: 400 }}>
            {t('ind.close')}
          </Typography>
          <CloseIcon style={{ fontSize: 20, color: '#404040' }} />
        </Box>
      </Box>

        {type === 'disponibilidad' && (
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <Typography style={{ fontSize: '0.9rem', color: '#404040', fontWeight: 500, marginBottom: 12 }}>
              {`${t('ind.hourly.subtitle')} ${t('ind.hourly.marked-hour-note')}`}
            </Typography>
          </Box>
        )}


      {/* Stat Boxes */}
      <Box display="flex" style={{ gap: 16 }} mb={3}>
        <Box style={{
          backgroundColor: '#F8F5FF',
          borderRadius: 16,
          padding: '14px 18px',
          minWidth: 120,
        }}>
          <Typography variant="caption" style={{
            color: '#9F9F9F',
            fontSize: '0.7rem',
            display: 'block',
            marginBottom: 2,
          }}>
            {type === 'disponibilidad' ? t('ind.hourly.acumulado-real') : t('ind.hourly.promedio-mes')}
          </Typography>
          <Typography style={{
            fontWeight: 700,
            fontSize: '1.7rem',
            lineHeight: 1.2,
            color: '#0A1938',
          }}>
            {avgReal}{unit}
          </Typography>
        </Box>
        {type === 'tiempo' && (
        <Box style={{
          backgroundColor: '#F8F5FF',
          borderRadius: 16,
          padding: '14px 18px',
          minWidth: 120,
        }}>
          <Typography variant="caption" style={{
            color: '#9F9F9F',
            fontSize: '0.7rem',
            display: 'block',
            marginBottom: 2,
          }}>
            {t('ind.hourly.p95')}
          </Typography>
          <Typography style={{
            fontWeight: 700,
            fontSize: '1.7rem',
            lineHeight: 1.2,
            color: '#0A1938',
          }}>
            {p95}{unit}
          </Typography>
        </Box>
        )}
        {type === 'disponibilidad' && (
        <Box style={{
          backgroundColor: '#F8F5FF',
          borderRadius: 16,
          padding: '14px 18px',
          minWidth: 150,
        }}>
          <Typography variant="caption" style={{
            color: '#9F9F9F',
            fontSize: '0.7rem',
            display: 'block',
            marginBottom: 2,
          }}>
            {t('ind.hourly.acumulado-programado')}
          </Typography>
          <Typography style={{
            fontWeight: 700,
            fontSize: '1.7rem',
            lineHeight: 1.2,
            color: '#0A1938',
          }}>
            {avgProgramado}{unit}
          </Typography>
        </Box>
        )}
      </Box>

      {/* Content */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <AtCircularProgress />
        </Box>
      )}
      {error && (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {!loading && !error && days.length > 0 && (
        <Box style={{
          borderRadius: 8,
          overflow: 'auto',
          maxHeight: '65vh',
        }}>
          <table style={{
            borderCollapse: 'collapse',
            fontSize: '0.7rem',
            minWidth: '100%',
          }}>
            <thead>
              <tr>
                <th style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  backgroundColor: '#0A1938',
                  color: '#fff',
                  fontWeight: 600,
                  padding: '6px 10px',
                  minWidth: 76,
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}>
                  {t('ind.hourly.hora')}
                </th>
                {days.map(dia => (
                  <th key={dia} style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#0A1938',
                    color: '#fff',
                    fontWeight: 600,
                    padding: '6px 8px',
                    minWidth: 88,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                    {type === 'disponibilidad' ? (
                      <HourlyDetailTableFormatDayHeader 
                        diaStr={dia}
                        locale={locale}
                        dayCodes={dayCodes[dia] ? Array.from(dayCodes[dia]) : []}
                      />
                    ) : (
                      <HourlyDetailTableFormatDayHeader 
                        diaStr={dia}
                        locale={locale}
                        dayCodes={[]}
                        disablePopover
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOUR_LABELS.map((label, hourIdx) => (
                <tr key={hourIdx}>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: hourIdx % 2 === 0 ? '#EFF3FE' : '#fff',
                    color: '#0A1938',
                    fontWeight: 500,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                  }}>
                    {label}
                  </td>
                  {days.map((dia, dayI) => {
                    const cell = grid[hourIdx]?.[dayI];
                    const baseBg = hourIdx % 2 === 0 ? '#EFF3FE' : '#fff';
                    const hasMarked = type === 'disponibilidad' && cell?.hasMarked;
                    // Para disponibilidad muestra disponibilidad_programada como métrica
                    // principal si no viene, usa Real para no dejar la celda vacía.
                    const displayVal = type === 'disponibilidad'
                      ? (isValidNumber(cell?.programado) ? cell.programado : cell?.value)
                      : cell?.value;
                    const colorBg = type === 'disponibilidad' && isValidNumber(displayVal)
                      ? getAvailabilityBg(displayVal, { skipGreen: true })
                      : null;
                    return (
                      <td key={dia} style={{
                        backgroundColor: hasMarked ? MARKED_HOUR_BG : (colorBg || baseBg),
                        color: '#0A1938',
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}>
                        {isValidNumber(displayVal) ? (
                          hasMarked ? (
                            <Tooltip
                              arrow
                              placement="top"
                              PopperProps={{
                                style: { zIndex: 11000 },
                              }}
                              title={(
                                <Box>
                                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>
                                    {t('ind.hourly.marked-tooltip-title')}
                                  </Typography>
                                  <Typography style={{ fontSize: '0.72rem', color: '#fff' }}>
                                    {`${t('ind.hourly.real')}: ${isValidNumber(cell?.value) ? `${cell.value.toFixed(2)}%` : '—'}`}
                                  </Typography>
                                  <Typography style={{ fontSize: '0.72rem', color: '#fff' }}>
                                    {`${t('ind.hourly.programado')}: ${isValidNumber(cell?.programado) ? `${cell.programado.toFixed(2)}%` : '—'}`}
                                  </Typography>
                                </Box>
                              )}
                            >
                              <span style={{ cursor: 'help' }}>{`${displayVal.toFixed(2)}${unit}`}</span>
                            </Tooltip>
                          ) : `${displayVal.toFixed(2)}${unit}`
                        ) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Real row */}
              {type === 'disponibilidad' && (
                <tr>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    color: '#0A1938',
                    fontWeight: 600,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                  }}>
                    {t('ind.hourly.real')}
                  </td>
                  {days.map((dia, dayI) => {
                    const val = dayRealAvg[dayI];
                    const colorBg = val != null ? getAvailabilityBg(val) : null;
                    return (
                      <td key={dia} style={{
                        backgroundColor: colorBg || '#fff',
                        color: '#0A1938',
                        fontWeight: 600,
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}>
                        {val != null ? `${val.toFixed(2)}${unit}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}
              {/* Programado row */}
              {type === 'disponibilidad' && (
                <tr>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    color: '#0A1938',
                    fontWeight: 600,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                  }}>
                    {t('ind.hourly.programado')}
                  </td>
                  {days.map((dia, dayI) => {
                    const val = dayProgAvg[dayI];
                    const colorBg = val != null ? getAvailabilityBg(val) : null;
                    return (
                      <td key={dia} style={{
                        backgroundColor: colorBg || '#fff',
                        color: '#0A1938',
                        fontWeight: 600,
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}>
                        {val != null ? `${val.toFixed(2)}${unit}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}
              {/* Promedio row (tiempo) */}
              {type === 'tiempo' && (
                <tr>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    color: '#0A1938',
                    fontWeight: 600,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                  }}>
                    {t('ind.hourly.promedio-dia')}
                  </td>
                  {days.map((dia, dayI) => {
                    const val = dayAvg[dayI];
                    return (
                      <td key={dia} style={{
                        backgroundColor: '#fff',
                        color: '#0A1938',
                        fontWeight: 600,
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}>
                        {val != null ? `${val.toFixed(2)}${unit}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}
              {/* P95 row (tiempo) */}
              {type === 'tiempo' && (
                <tr>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    color: '#0A1938',
                    fontWeight: 600,
                    padding: '4px 10px',
                    whiteSpace: 'nowrap',
                    borderBottom: 'none',
                  }}>
                    {t('ind.hourly.p95')}
                  </td>
                  {days.map((dia, dayI) => {
                    const val = dayP95[dayI];
                    return (
                      <td key={dia} style={{
                        backgroundColor: '#fff',
                        color: '#0A1938',
                        fontWeight: 600,
                        padding: '4px 8px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                      }}>
                        {val != null ? `${val.toFixed(2)}${unit}` : '—'}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      )}
      {!loading && !error && days.length === 0 && hourlyData && (
        <Box display="flex" justifyContent="center" py={4}>
          <Typography style={{ color: '#9F9F9F' }}>
            {t('ind.hourly.no-data')}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}

export default HourlyDetailTable;
